import { useCallback, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { mergeClasses } from '@fluentui/react-components';
import type { RailState } from '@/types/leftNavRail.types';
import { telemetry } from '@services/telemetry';
import { TELEMETRY_COMPONENT, EVENT_TYPES } from '@/constants';
import { useWaffleToggleStyles } from './WaffleToggle.styles';
import waffleIconUrl from './waffle-icon.svg';

export interface WaffleToggleProps {
  readonly state: RailState;
  readonly onToggle: () => void;
}

export const WaffleToggle = ({ state, onToggle }: WaffleToggleProps) => {
  const styles = useWaffleToggleStyles();
  const { t } = useTranslation();
  const isExpanded = state === 'expanded';
  const label = t(isExpanded ? 'leftNav.toggleCollapse' : 'leftNav.toggleExpand');

  const fireToggle = useCallback(() => {
    const next: RailState = isExpanded ? 'collapsed' : 'expanded';
    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.UI_INTERACTION, {
      source: 'leftNavRail.toggle',
      from: state,
      to: next,
    });
    onToggle();
  }, [isExpanded, state, onToggle]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        fireToggle();
      }
    },
    [fireToggle]
  );

  return (
    <button
      type="button"
      className={mergeClasses(styles.root, isExpanded ? styles.rootExpanded : styles.rootCollapsed)}
      onClick={fireToggle}
      onKeyDown={handleKeyDown}
      data-testid="header-waffle-toggle"
      aria-label={label}
      aria-pressed={isExpanded}
      aria-controls="left-nav-rail"
    >
      <span className={styles.iconColumn} aria-hidden="true">
        <img src={waffleIconUrl} alt="" aria-hidden="true" className={styles.icon} />
      </span>
    </button>
  );
};

WaffleToggle.displayName = 'WaffleToggle';
